/**
 * @fileoverview Componente responsável por renderizar nós individuais no construtor de fluxos.
 */

import React, { useContext, useState } from "react";
import { Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Check, X, Plus, Send as SendIcon } from "lucide-react";
import { Handle, Position, useStore } from "react-flow-renderer";

import {
  NODE_TYPES_CONFIG, TRIGGER_TYPES, TOOL_TYPES, TOOL_ITEMS,
  NODE_ICON_COLOR, NODE_ICON_CHIP,
} from "./constants";
import useStyles from "./styles";
import usePermission from "../../../hooks/usePermission";
import { AgentOptionsContext, KNOWLEDGE_TOOL_TYPES, agentNodeMode } from "./agentNode";

// Status escolhidos no "Status da OS", para o subtítulo do nó.
var orderStatusSummary = function (statuses) {
  var names = (Array.isArray(statuses) ? statuses : [])
    .map(function (s) { return s && s.name; })
    .filter(Boolean);
  if (names.length === 0) return "Escolha o status";
  if (names.length <= 2) return names.join(", ");
  return names.slice(0, 2).join(", ") + " e mais " + (names.length - 2);
};

// O subtítulo do "Agente IA": o agente que atende, o "Personalizado" ou o que falta.
// Sem a lista (o percurso nos resultados), fica a descrição de sempre.
var agentSummary = function (data, agents, fallback) {
  var mode = agentNodeMode(data);
  if (mode === "custom") return "Personalizado";
  if (mode === "empty") return "Escolha o agente";
  if (!agents) return fallback;
  var agent = agents.find(function (a) { return a.id === Number(data.agentId); });
  return agent ? agent.name : "Escolha o agente";
};

/* ─── Custom Node Component ──────────────────────────── */
var FlowNode = function (props) {
  var data = props.data;
  var cfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === data.nodeType; }) || NODE_TYPES_CONFIG[0];
  var Icon = cfg.icon;
  var isTrigger = TRIGGER_TYPES.indexOf(cfg.type) !== -1;
  var isTool = TOOL_TYPES.indexOf(cfg.type) !== -1;
  var isAgent = cfg.type === "ai_agent";
  var isTemplate = cfg.type === "send_template";
  var isTriage = cfg.type === "ai_triage";
  var isOrderStatus = cfg.type === "order_status";
  var agentOptions = useContext(AgentOptionsContext);
  // Com agente (ou sem nada escolhido), arquivos, casos, pesquisa e loja não valem:
  // o "+" do bloco só oferece o que é do fluxo (o CRM).
  var toolItems = isAgent && agentNodeMode(data) !== "custom"
    ? TOOL_ITEMS.filter(function (tool) { return KNOWLEDGE_TOOL_TYPES.indexOf(tool.type) === -1; })
    : TOOL_ITEMS;
  // "Enviar Mensagem" ligado ao "Status da OS": os números ficam na página de
  // resultados do fluxo (os contadores do nó são do disparo em massa e ficariam
  // sempre em zero). O seletor devolve só o sim/não: o nó não redesenha a cada
  // mudança do desenho.
  var nodeId = props.id;
  var viaOrderStatus = useStore(function (s) {
    if (!isTemplate) return false;
    return s.edges.some(function (e) {
      if (e.target !== nodeId) return false;
      var source = s.nodeInternals.get(e.source);
      return !!(source && source.data && source.data.nodeType === "order_status");
    });
  });
  // Só leitura (percurso de uma execução, na página de resultados): o nó é
  // desenhado igual, sem o botão de ferramentas e sem os contadores do template.
  var readOnly = data._readOnly === true;
  // Sem flows.edit (com a regra nova ligada), o editor abre só para ver: o nó
  // do agente fica sem o botão de ferramentas, como no percurso.
  var canEdit = usePermission().can("flows.edit", true);
  var noTools = readOnly || !canEdit;
  var classes = useStyles();
  var isDark = useTheme().palette.type === "dark";
  // Corpo do node theme-aware: branco-com-alpha → escuro-com-mesmo-alpha no
  // claro (o card fica branco no tema claro, então o texto some sem isso).
  var W = function (a) { return isDark ? "rgba(255,255,255," + a + ")" : "rgba(20,22,30," + a + ")"; };

  var popupState = useState(false);
  var showPopup = popupState[0];
  var setShowPopup = popupState[1];

  var handlePlusClick = function (e) {
    e.stopPropagation();
    setShowPopup(function (prev) { return !prev; });
  };

  var handleToolSelect = function (toolType) {
    setShowPopup(false);
    var event = new CustomEvent("flow:addTool", {
      detail: { agentNodeId: props.id, toolType: toolType },
    });
    window.dispatchEvent(event);
  };

  var execStatus = data._executionStatus;
  var execGlow = execStatus === "running"
    ? "0 0 0 3px rgba(245,158,11,0.5)"
    : execStatus === "completed"
    ? "0 0 0 3px rgba(34,197,94,0.5)"
    : execStatus === "error"
    ? "0 0 0 3px rgba(239,68,68,0.5)"
    : "none";

  return (
    <div style={{
      minWidth: isTemplate ? 240 : 180,
      maxWidth: isTemplate ? 280 : undefined,
      position: "relative",
      borderRadius: 12,
      boxShadow: execGlow,
      transition: "box-shadow 0.3s ease",
    }}>
      {/* Input handle (top) — not for triggers */}
      {!isTrigger && (
        <Handle
          type="target"
          position={Position.Top}
          id="input"
          style={{ top: -4 }}
        />
      )}

      {/* Execution indicator */}
      {execStatus === "running" && (
        <div style={{
          position: "absolute", top: -6, right: -6, width: 14, height: 14,
          borderRadius: "50%", background: "#f59e0b",
          animation: "pulse 1.2s ease-in-out infinite",
          zIndex: 5,
        }} />
      )}
      {execStatus === "completed" && (
        <div style={{
          position: "absolute", top: -6, right: -6, width: 14, height: 14,
          borderRadius: "50%", background: "#22c55e",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 5,
        }}>
          <Check size={8} color="#fff" strokeWidth={3} />
        </div>
      )}
      {execStatus === "error" && (
        <div style={{
          position: "absolute", top: -6, right: -6, width: 14, height: 14,
          borderRadius: "50%", background: "#ef4444",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 5,
        }}>
          <X size={8} color="#fff" strokeWidth={3} />
        </div>
      )}

      {/* Node body */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: NODE_ICON_CHIP(isDark),
            flexShrink: 0,
          }}
        >
          <Icon size={14} color={NODE_ICON_COLOR(isDark)} strokeWidth={2} />
        </div>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.2,
              marginBottom: 1,
            }}
          >
            {data.label || cfg.label}
          </div>
          <div
            style={{
              fontSize: 10,
              lineHeight: 1.2,
              opacity: 0.6,
              maxWidth: isAgent ? 200 : undefined,
              overflow: isAgent ? "hidden" : undefined,
              textOverflow: isAgent ? "ellipsis" : undefined,
              whiteSpace: isAgent ? "nowrap" : undefined,
            }}
          >
            {isTriage
              ? data.definitionName || "Escolha a triagem"
              : isOrderStatus
              ? orderStatusSummary(data.statuses)
              : isAgent
              ? agentSummary(data, agentOptions, cfg.desc)
              : cfg.desc}
          </div>
        </div>
      </div>

      {/* Template node: metrics + preview + buttons CTR */}
      {isTemplate && (function () {
        var template = data.selectedTemplate;
        var stats = data.stats || {};
        var sent = stats.sent || 0;
        var delivered = stats.delivered || 0;
        var read = stats.read || 0;
        var deliveryPct = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : "0.0";
        var readPct = sent > 0 ? ((read / sent) * 100).toFixed(1) : "0.0";

        return (
          <div style={{ padding: "0 10px 10px" }}>
            {/* Metrics row */}
            {!readOnly && !viaOrderStatus && <div style={{
              display: "flex", justifyContent: "space-around", padding: "6px 0 8px",
              borderBottom: "1px solid " + W(0.06), marginBottom: 8,
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>{sent}</div>
                <div style={{ fontSize: 9, opacity: 0.5 }}>Enviado</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>{deliveryPct}%</div>
                <div style={{ fontSize: 9, opacity: 0.5 }}>Entregue</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>{readPct}%</div>
                <div style={{ fontSize: 9, opacity: 0.5 }}>Lido</div>
              </div>
            </div>}

            {/* Template preview */}
            {template ? (
              <div style={{
                background: W(0.04), borderRadius: 8,
                padding: "8px 10px", fontSize: 11, lineHeight: 1.5,
                color: W(0.7), whiteSpace: "pre-wrap",
                maxHeight: 120, overflow: "hidden",
              }}>
                {template.bodyText || "(Sem conteudo)"}
              </div>
            ) : (
              <div style={{
                background: W(0.03), borderRadius: 8,
                padding: "12px 10px", fontSize: 10, textAlign: "center",
                color: W(0.5),
              }}>
                Selecione um template
              </div>
            )}

            {/* Template buttons with CTR */}
            {template && template.buttons && template.buttons.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                {template.buttons.map(function (btn, idx) {
                  var btnTitle = btn.text || btn.title || "";
                  var allBtnStats = stats.buttons || [];
                  // Match SOMENTE por titulo/id. O fallback antigo (allBtnStats[idx])
                  // atribuia o clique de UM botao a OUTRO quando o botao sem clique
                  // nao estava em stats.buttons — ex.: "Nao tenho interesse" com 2
                  // cliques aparecia tambem em "Sim, faz sentido". Sem clique => 0%.
                  var btnStats = allBtnStats.find(function (b) { return b.title === btnTitle || b.id === btnTitle; }) || {};
                  var btnClicks = btnStats.clicks || 0;
                  var btnCtr = sent > 0 ? ((btnClicks / sent) * 100).toFixed(1) : "0.0";
                  var isUrl = btn.type === "URL";
                  return (
                    <div key={idx} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "6px 10px", borderRadius: 6,
                      border: "1px solid rgba(34,197,94,0.15)",
                      background: "rgba(34,197,94,0.04)",
                    }}>
                      <span style={{ fontSize: 11, color: W(0.7) }}>{btnTitle}</span>
                      {/* Só leitura: a cópia do desenho não guarda contadores; "CTR 0%" seria um número inventado. */}
                      {(isUrl || !readOnly) && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: isUrl ? W(0.3) : "#22c55e" }}>
                          {isUrl ? "URL" : "CTR " + btnCtr + "%"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Agent node: main output (right) + tools "+" button */}
      {isAgent && (
        <React.Fragment>
          <Handle
            type="source"
            position={Position.Right}
            id="output"
            style={{ right: -4 }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="tools"
            style={{ bottom: -40, opacity: 0, width: 26, height: 26 }}
          />
          <div
            style={{
              position: "absolute",
              // Só leitura: sem o botão "+", o traço vai até o ponto de onde saem as ferramentas.
              bottom: noTools ? -27 : -14,
              left: "50%",
              transform: "translateX(-50%)",
              width: 1,
              height: noTools ? 27 : 14,
              background: noTools ? W(0.3) : "rgba(139, 92, 246, 0.3)",
              pointerEvents: "none",
            }}
          />
          {!noTools && <Tooltip title="Ferramentas" placement="right">
            <div
              onClick={handlePlusClick}
              style={{
                position: "absolute",
                bottom: -42,
                left: "50%",
                transform: "translateX(-50%)",
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "#1a1c26",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
                zIndex: 2,
              }}
            >
              <Plus size={14} color="#8b5cf6" strokeWidth={2.5} />
            </div>
          </Tooltip>}
          {showPopup && (
            <div className={classes.toolsPopup} style={{ top: "auto", bottom: -42 - 8 - (toolItems.length * 40 + 12), left: "50%", transform: "translateX(-50%)" }}>
              {toolItems.map(function (tool) {
                var ToolIcon = tool.icon;
                return (
                  <div
                    key={tool.type}
                    className={classes.toolsPopupItem}
                    onClick={function (e) { e.stopPropagation(); handleToolSelect(tool.type); }}
                  >
                    <div className={classes.toolsPopupIcon} style={{ background: NODE_ICON_CHIP(isDark) }}>
                      <ToolIcon size={13} color={NODE_ICON_COLOR(isDark)} strokeWidth={2} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{tool.label}</div>
                      <div style={{ fontSize: 10, opacity: 0.5 }}>{tool.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </React.Fragment>
      )}

      {/* Normal nodes (not agent, not tool, not trigger): output at bottom.
          "Triagem IA" é o fim do fluxo: sem saída (nada roda depois dela). */}
      {!isAgent && !isTool && !isTriage && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="output"
          style={{ bottom: -4 }}
        />
      )}
    </div>
  );
};

export default FlowNode;
